import { LAST_ACTIVITY_KEY, LOGIN_AT_KEY } from "@/lib/sessionTimeout";
// Force rebuild - 2025-11-14
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Compass } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import aciLogo from "@/assets/aci-logo-auth.jpeg";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, ArrowLeft } from "lucide-react";

// Configuration: Allowed email domains for registration
const ALLOWED_DOMAINS = ['creativetravel.africa', 'creativeinvestments.africa', 'snapstack.co.za', 'coffeebeansroutes.com', 'coffeebeansexperiences.africa'] as const;
const ADMIN_EMAILS = ['oneiain@icloud.com', 'natasha@creativeinvestments.africa'] as const;
// Sign in schema - no domain restriction (existing users can sign in from any email)
const signInSchema = z.object({
  email: z.string().email("Invalid email address").trim(),
  password: z.string().min(6, "Password must be at least 6 characters")
});

// Sign up schema - with domain restriction for new registrations
const signUpSchema = z.object({
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Invalid email address").trim().refine(email => {
    const domain = email.toLowerCase().split('@')[1];
    return ADMIN_EMAILS.includes(email.toLowerCase() as any) || ALLOWED_DOMAINS.includes(domain as any);
  }, {
    message: "Registration is restricted to authorized organization domains only."
  }),
  password: z.string().min(6, "Password must be at least 6 characters")
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address").trim()
});
const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters")
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});
type SignInFormData = z.infer<typeof signInSchema>;
type SignUpFormData = z.infer<typeof signUpSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const params = new URLSearchParams(location.search);
  const timedOut = params.get("reason") === "timeout";
  const nextPath = () => {
    const n = params.get("next");
    return n && n.startsWith("/") && !n.startsWith("//") && !n.startsWith("/auth") ? n : "/";
  };
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  // Use ref for synchronous recovery detection to prevent race conditions
  const isRecoveryRef = useRef(false);
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema)
  });
  const {
    register: registerSignUp,
    handleSubmit: handleSubmitSignUp,
    formState: {
      errors: signUpErrors
    },
    reset: resetSignUp
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema)
  });
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: {
      errors: forgotErrors
    },
    reset: resetForgot
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema)
  });
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: {
      errors: resetErrors
    }
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema)
  });
  useEffect(() => {
    // Check for password reset token in URL FIRST (synchronous ref update)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    if (accessToken && type === 'recovery') {
      // Set ref immediately (synchronous) to prevent race condition
      isRecoveryRef.current = true;
      setIsResettingPassword(true);
      // Don't check session or set up redirect - user must set new password
      return;
    }

    // Check if user is already logged in (only if not in recovery mode)
    if (!isRecoveryRef.current) {
      supabase.auth.getSession().then(({
        data: {
          session
        }
      }) => {
        if (session && !isRecoveryRef.current) {
          navigate(nextPath());
        }
      });
    }

    // Set up auth state listener
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle password recovery event specifically
      if (event === 'PASSWORD_RECOVERY') {
        isRecoveryRef.current = true;
        setIsResettingPassword(true);
        return;
      }
      
      if (event === 'SIGNED_IN') {
        const t = String(Date.now());
        localStorage.setItem(LOGIN_AT_KEY, t);
        localStorage.setItem(LAST_ACTIVITY_KEY, t);
      }
      // Only navigate if NOT in recovery mode (check both ref and state)
      if (session && !isRecoveryRef.current && !isResettingPassword) {
        navigate(nextPath());
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, isResettingPassword]);
  const onSignUp = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/`;
      const {
        error
      } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: data.fullName
          }
        }
      });
      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: "Account exists",
            description: "This email is already registered. Please sign in instead.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Sign up failed",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Success!",
          description: "Account created successfully. You can now sign in."
        });
        resetSignUp();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const onSignIn = async (data: SignInFormData) => {
    setIsLoading(true);
    console.log("Attempting sign in...");
    try {
      const {
        data: authData,
        error
      } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });
      if (error) {
        console.error("Sign in error:", error);
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Sign in failed",
            description: "Invalid email or password. Please try again.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Sign in failed",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        console.log("Sign in successful, session:", authData.session);
        toast({
          title: "Success!",
          description: "Signed in successfully. Redirecting..."
        });
        // Wait a moment for session to be fully established
        setTimeout(() => {
          navigate(nextPath());
        }, 100);
      }
    } catch (error) {
      console.error("Unexpected error during sign in:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const onForgotPassword = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      const redirectUrl = `${window.location.origin}/auth`;
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: redirectUrl
      });
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        setResetEmailSent(true);
        toast({
          title: "Email sent!",
          description: "Check your email for the password reset link."
        });
        resetForgot();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const onResetPassword = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: data.password
      });
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success!",
          description: "Your password has been updated successfully."
        });
        // Clear recovery mode
        isRecoveryRef.current = false;
        setIsResettingPassword(false);
        // Clear the hash from URL
        window.history.replaceState(null, '', '/auth');
        setTimeout(() => {
          navigate(nextPath());
        }, 100);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-2">
          <img src={aciLogo} alt="African Creative Investments Logo" className="h-32 w-auto mb-2" />
          <p className="text-muted-foreground">Company Dashboard</p>
          {timedOut && <p className="text-sm text-muted-foreground border rounded-md px-3 py-2 mt-2">You were signed out for security. Please sign in again.</p>}
        </div>

        {isResettingPassword ? <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>Enter your new password below</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitReset(onResetPassword)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input id="new-password" type="password" placeholder="••••••••" {...registerReset("password")} />
                  {resetErrors.password && <p className="text-sm text-destructive">{resetErrors.password.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input id="confirm-password" type="password" placeholder="••••••••" {...registerReset("confirmPassword")} />
                  {resetErrors.confirmPassword && <p className="text-sm text-destructive">{resetErrors.confirmPassword.message}</p>}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Updating password..." : "Update Password"}
                </Button>
              </form>
            </CardContent>
          </Card> : <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle>Welcome</CardTitle>
              <CardDescription>Sign in to your account or create a new one</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="signin">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSubmit(onSignIn)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input id="signin-email" type="email" placeholder="your@email.com" {...register("email")} />
                      {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="signin-password">Password</Label>
                        <Button type="button" variant="link" className="h-auto p-0 text-sm text-primary" onClick={() => setShowForgotPassword(true)}>
                          Forgot password?
                        </Button>
                      </div>
                      <Input id="signin-password" type="password" placeholder="••••••••" {...register("password")} />
                      {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSubmitSignUp(onSignUp)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-fullname">Full Name</Label>
                      <Input id="signup-fullname" type="text" placeholder="Your full name" {...registerSignUp("fullName")} />
                      {signUpErrors.fullName && <p className="text-sm text-destructive">{signUpErrors.fullName.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input id="signup-email" type="email" placeholder="your@email.com" {...registerSignUp("email")} />
                      {signUpErrors.email && <p className="text-sm text-destructive">{signUpErrors.email.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input id="signup-password" type="password" placeholder="••••••••" {...registerSignUp("password")} />
                      {signUpErrors.password && <p className="text-sm text-destructive">{signUpErrors.password.message}</p>}
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "Creating account..." : "Sign Up"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>}

        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={open => {
        setShowForgotPassword(open);
        if (!open) {
          setResetEmailSent(false);
          resetForgot();
        }
      }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                {resetEmailSent ? "Check your email for the password reset link" : "Enter your email address and we'll send you a link to reset your password"}
              </DialogDescription>
            </DialogHeader>
            
            {resetEmailSent ? <div className="space-y-4">
                <Alert>
                  <Mail className="h-4 w-4" />
                  <AlertDescription>
                    We've sent a password reset link to your email address. 
                    Click the link in the email to set a new password.
                  </AlertDescription>
                </Alert>
                <Button variant="outline" className="w-full" onClick={() => {
              setShowForgotPassword(false);
              setResetEmailSent(false);
              resetForgot();
            }}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </div> : <form onSubmit={handleSubmitForgot(onForgotPassword)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" type="email" placeholder="your@email.com" {...registerForgot("email")} autoFocus />
                  {forgotErrors.email && <p className="text-sm text-destructive">{forgotErrors.email.message}</p>}
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => {
                setShowForgotPassword(false);
                resetForgot();
              }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                </div>
              </form>}
          </DialogContent>
        </Dialog>
      </div>
    </div>;
};
export default Auth;